import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import { CommonlySearchedCard } from '../../../../common/CommonlySearched';
import { Constants } from '../../../../common/Constants';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { PSXHeader } from '../../../../common/PSXHeader';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { StillNeedHelp } from '../../../../common/StillNeedHelp';
import { convertObjectToUrlParams } from '../../../../common/Utils';
import { getSearchLocation } from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useGeoLocationStorage } from '../../../../hooks/useGeoLocationStorage';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { NoSpecialtyResult } from './NoSpecialtyResult';
import { NullPageContainerStyled } from './NullSpecialtyPage.styles';

export type NullResultsPageProps = {
  searchTitle: string;
};

export const NullResultPageForSpecialty = () => {
  const isFirstRender = useRef(true);
  const { navigate } = useRouter();
  const [searchParams] = useSearchParams();
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'null specialty results',
    sitesectionLevel1: `${Constants.ADOBE_TRACKING.VIEWALL_SITESECTION1}`,
    sitesectionLevel2: `${Constants.ADOBE_TRACKING.VIEWALL_SITESECTION2}`,
  });
  const { longitude, latitude } = useGeoLocationStorage();

  const { search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
    }))
  );
  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const searchParamsObject = Object.fromEntries(searchParams.entries());
    const urlParams = convertObjectToUrlParams({} as ChipState, {
      ...searchParamsObject,
      [Constants.PRE_SELECT_FILTERS]: true,
    });
    navigate(
      `${ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path}/${urlParams}`
    );
  }, [latitude, longitude]);
  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  const { t } = useTranslation();
  const storedLocation = getGeoLocationFromStorage();

  const breadcrumbs = [
    {
      title: t('BC Results'),
      href: ``,
    },
  ];

  useEffect(() => {
    adobeStandardSearchEvent({
      linkName,
      numberOfResults: 0,
      method: 'guided',
      customAttributesBlock: {
        searchLocation: getSearchLocation(storedLocation),
      },
    });
  }, []);
  return (
    <React.Fragment>
      <PSXHeader
        breadcrumbs={breadcrumbs}
        dataTestId="no-specialty-page"
        showChips={false}
        showChoosePCPHeader={false}
        showMemberSelection={false}
      />
      <NullPageContainerStyled>
        <NoSpecialtyResult
          data-auto-testid="null-specialty-page"
          data-testid="null-specialty-page"
          searchName={search || ''}
        />
        <CommonlySearchedCard />
        <StillNeedHelp />
      </NullPageContainerStyled>
    </React.Fragment>
  );
};
